import React, { useEffect, useRef, useState } from "react";
import {
  ArrowDown,
  CircleCheckBig,
  ChevronUp,
  History,
  Minus,
  Plus,
  ShoppingBag,
  ShoppingCart,
  Trash2,
} from "lucide-react";
import useCtx from "../hooks/useCts";
import TopNavigator from "../components/TopNavigator";
import Footer from "../components/Footer";
import { createOrder, deleteOrder } from "../api/orders";
import ModalConfirm from "../components/ModalConfirm";
import { updateClient } from "../api/clients";
import BookCardKids from "../components/BookCardKidsOrders";
import BookCard from "../components/BookCardOrders";
import Skyline from "../assets/london-skyline.jpg";
import { useLocation } from "react-router-dom";
import {
  ACTION_BUTTON_TEXT_CLASS_NAME,
  getActionButtonStyle,
} from "../utils/actionButtonStyles";

const OrderScreen: React.FC = () => {
  const ctx = useCtx();
  const location = useLocation();
  const userClient = ctx.clients.find(
    (client) => client.authId === ctx.user.uid,
  );
  const [selectedBooks, setSelectedBooks] = useState<
    { bookId: string; quantity: number }[]
  >([]);
  const [note, setNote] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState<"order" | "history">("order");
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isOrderSuccessModalVisible, setIsOrderSuccessModalVisible] =
    useState(false);
  const [isCartExpanded, setIsCartExpanded] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState<string | null>(null);
  const hasHandledInitialScroll = useRef(false);

  const handleCancelOrder = (orderId: string) => {
    setOrderToCancel(orderId);
    setIsModalVisible(true);
  };

  const confirmCancelOrder = async () => {
    if (orderToCancel) {
      try {
        await deleteOrder(orderToCancel);
        alert("Zamówienie zostało anulowane.");
        setIsModalVisible(false);
      } catch (error) {
        console.error("Błąd podczas anulowania zamówienia", error);
        alert("Wystąpił błąd podczas anulowania zamówienia.");
      }
    }
  };

  const cancelCancelOrder = () => {
    setIsModalVisible(false);
  };

  const handleOrderSuccessAcknowledge = () => {
    setIsOrderSuccessModalVisible(false);
    setActiveTab("order");
    setError("");
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const handleQuantityChange = (bookId: string, quantity: number) => {
    setSelectedBooks((prev) => {
      let updated = [...prev];
      const index = updated.findIndex((b) => b.bookId === bookId);

      if (index !== -1) {
        if (quantity === 0) {
          updated.splice(index, 1);
        } else {
          updated[index].quantity = quantity;
        }
      } else if (quantity > 0) {
        updated.push({ bookId, quantity });
      }
      return updated;
    });
  };

  const calculateTotal = () => {
    const clientDiscounts = userClient?.discounts || [];

    let totalBeforeDiscount = selectedBooks.reduce((total, sb) => {
      const book =
        ctx.books.find((b) => b.id === sb.bookId) ||
        ctx.booksKids.find((b) => b.id === sb.bookId);
      if (book?.price) {
        total += book.price * sb.quantity;
      }
      return total;
    }, 0);

    let totalAfterDiscount = selectedBooks.reduce((total, sb) => {
      const book =
        ctx.books.find((b) => b.id === sb.bookId) ||
        ctx.booksKids.find((b) => b.id === sb.bookId);
      if (book?.price) {
        let price = book.price;
        if (clientDiscounts.length > 0) {
          const discountPercent = clientDiscounts.reduce(
            (sum, d) => sum + d.valuePercent,
            0,
          );
          price = price - (price * discountPercent) / 100;
        }
        total += price * sb.quantity;
      }
      return total;
    }, 0);

    return {
      totalBeforeDiscount,
      totalAfterDiscount,
      discountDifference: totalBeforeDiscount - totalAfterDiscount,
    };
  };

  const { totalBeforeDiscount, totalAfterDiscount, discountDifference } =
    calculateTotal();

  const placeOrder = async () => {
    if (!userClient) {
      setError("Nie znaleziono konta użytkownika.");
      return;
    }
    if (selectedBooks.length === 0) {
      setError("Wybierz przynajmniej jedną książkę.");
      return;
    }

    const filteredBooks = selectedBooks.filter((b) => b.quantity > 0);

    if (filteredBooks.length === 0) {
      setError("Nie ma książek do zamówienia.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const orderData: Omit<Order, "id"> = {
        clientId: userClient.id,
        date: Date.now(),
        values: filteredBooks.map((b) => ({
          bookId: b.bookId,
          quantity: b.quantity,
        })),
        discount: discountDifference,
        price: totalAfterDiscount,
        status: "pending",
        amountPaid: 0,
        paid: false,
        note: note ?? "",
      };

      const createdOrder = await createOrder(orderData);
      if (createdOrder instanceof Error) {
        throw createdOrder;
      }

      setSelectedBooks([]);
      setNote("");
      setIsCartExpanded(false);
      setActiveTab("order");
      setIsOrderSuccessModalVisible(true);
    } catch (e) {
      console.error(e);
      setError("Wystąpił błąd podczas składania zamówienia.");
    } finally {
      setLoading(false);
    }
  };

  const salesPaused = false;
  const clientOrderCount = ctx.orders.filter(
    (order) => order.clientId === userClient?.id,
  ).length;
  const selectedCopies = selectedBooks.reduce(
    (total, item) => total + item.quantity,
    0,
  );
  const scrollToOrderSection = (sectionId: string) => {
    if (activeTab !== "order") {
      setActiveTab("order");
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          document.getElementById(sectionId)?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
      });
      return;
    }

    document.getElementById(sectionId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };
  const handleResetAll = () => {
    setSelectedBooks([]);
    setError("");
    setIsCartExpanded(false);
  };
  const hasSelectedBooks = selectedBooks.length > 0;
  const discountPercent = (userClient?.discounts || []).reduce(
    (sum, discount) => sum + discount.valuePercent,
    0,
  );
  const kidsBookNames: Record<number, string> = {
    1: "Magic Village",
    2: "Jungle World",
    3: "Robo City",
    4: "Space Mission",
    5: "Time Travel",
  };
  const cartItems = selectedBooks
    .flatMap((selectedBook) => {
      const book =
        ctx.books.find((item) => item.id === selectedBook.bookId) ||
        ctx.booksKids.find((item) => item.id === selectedBook.bookId);

      if (!book) return [];

      const isAdultBook = "bookNum" in book;
      const isTeachersBook = isAdultBook && String(book.bookNum).length === 2;
      const displayNum = isAdultBook
        ? String(book.bookNum)[0]
        : String(book.num);
      const sortNum = Number(displayNum);
      const sortGroup = !isAdultBook ? 0 : isTeachersBook ? 2 : 1;
      const basePrice = book.price ?? 0;
      const unitPrice =
        basePrice - (basePrice * Math.max(discountPercent, 0)) / 100;

      return [
        {
          id: selectedBook.bookId,
          label: isAdultBook
            ? `English Unlocked ${displayNum}`
            : `Kids Book ${displayNum}`,
          subtitle: isAdultBook ? null : (kidsBookNames[book.num] ?? null),
          bookTypeLabel: isAdultBook
            ? isTeachersBook
              ? "Teacher's Book"
              : "Student's Book"
            : null,
          thumbnailSrc: isAdultBook
            ? isTeachersBook
              ? require(`../assets/Book-${displayNum}T.jpg`)
              : require(`../assets/Book-${displayNum}.jpg`)
            : require(`../assets/Kids${displayNum}.jpg`),
          quantity: selectedBook.quantity,
          total: unitPrice * selectedBook.quantity,
          sortGroup,
          sortNum,
        },
      ];
    })
    .sort(
      (a, b) =>
        a.sortGroup - b.sortGroup ||
        a.sortNum - b.sortNum ||
        a.label.localeCompare(b.label),
    );

  const handleCartToggle = () => {
    if (!hasSelectedBooks) return;

    setError("");
    setIsCartExpanded((prev) => !prev);
  };

  const handlePlaceOrderClick = () => {
    setError("");
    void placeOrder();
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(max-width: 639px)");
    const updateViewport = () => setIsMobileViewport(mediaQuery.matches);

    updateViewport();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateViewport);

      return () => {
        mediaQuery.removeEventListener("change", updateViewport);
      };
    }

    mediaQuery.addListener(updateViewport);

    return () => {
      mediaQuery.removeListener(updateViewport);
    };
  }, []);

  useEffect(() => {
    const sectionId = (location.state as { scrollToSectionId?: string } | null)
      ?.scrollToSectionId;

    if (!sectionId || hasHandledInitialScroll.current) return;

    hasHandledInitialScroll.current = true;
    setActiveTab("order");

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.getElementById(sectionId)?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    });
  }, [location.state]);

  useEffect(() => {
    if (!hasSelectedBooks || activeTab !== "order") {
      setIsCartExpanded(false);
    }
  }, [activeTab, hasSelectedBooks]);

  useEffect(() => {
    if (isMobileViewport && activeTab !== "order") {
      setActiveTab("order");
    }
  }, [activeTab, isMobileViewport]);

  const tabs = [
    {
      key: "order",
      label: "Składanie zamówień",
      description: "Wybierz podręczniki i złóż zamówienie",
      Icon: ShoppingBag,
      accentClassName: "bg-sky-500",
      iconClassName: "bg-sky-50 text-sky-700",
    },
    {
      key: "history",
      label: "Historia Twoich zamówień",
      description: "Status zamówień i wcześniejsze zamówienia",
      Icon: History,
      accentClassName: "bg-orange-500",
      iconClassName: "bg-orange-50 text-[#c55f0d]",
    },
  ] as const;

  return (
    <>
      <div
        className="relative min-h-screen overflow-hidden bg-[#f7f2ea]"
        style={{ minHeight: "90vh" }}
      >
        <div
          aria-hidden="true"
          className="fixed inset-0  bg-cover bg-center bg-no-repeat opacity-40"
          style={{ backgroundImage: `url(${Skyline})` }}
        />
        <div
          aria-hidden="true"
          className="fixed inset-0 bg-[linear-gradient(180deg,rgba(255,250,245,0.82),rgba(255,255,255,0.66)_24%,rgba(248,250,252,0.5))]"
        />
        <div
          aria-hidden="true"
          className="fixed inset-0 bg-[radial-gradient(circle_at_top_left,rgba(246,133,31,0.18),transparent_50%),radial-gradient(circle_at_88%_18%,rgba(44,150,233,0.24),transparent_54%),linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.24))]"
        />
        <div className="relative z-10">
          <TopNavigator />

          <main className="mx-auto w-full max-w-7xl px-4 pb-16 pt-6 sm:px-6 lg:px-8">
            <section className="grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_360px] lg:items-start">
              <div className="px-1 pt-4 sm:pt-8 lg:pt-12">
                <h1 className="max-w-3xl bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text pb-1 text-3xl font-semibold leading-[1.12] tracking-[-0.04em] text-transparent sm:text-4xl lg:text-5xl">
                  Zamówienia i historia zakupów.
                </h1>
                <h2 className="mt-5 max-w-3xl font-medium leading-8 tracking-[-0.03em] text-slate-700 sm:text-2xl sm:leading-9 lg:text-[1.9rem]">
                  <span className="sm:hidden">
                    Wybierz podręczniki i wygodnie złóż zamówienie.
                  </span>
                  <span className="hidden sm:inline">
                    Wybierz podręczniki, złóż zamówienie i śledź historię
                    zakupów.
                  </span>
                </h2>

                <div className="mt-5 space-y-3 sm:hidden">
                  <button
                    type="button"
                    onClick={() => scrollToOrderSection("order-kids-students")}
                    className="flex w-full items-center justify-between gap-4 rounded-[22px] border border-white/50 bg-white/10 px-4 py-3 text-left shadow-[0_16px_36px_rgba(148,163,184,0.14)] backdrop-blur-sm transition hover:bg-white/20"
                  >
                    <span className="min-w-0 text-sm font-medium text-slate-700">
                      Zamów Kids Student&apos;s Book
                    </span>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-transparent bg-[linear-gradient(135deg,#38bdf8,#0ea5e9)] text-white shadow-[0_10px_24px_rgba(14,165,233,0.2)]">
                      <ArrowDown
                        size={18}
                        strokeWidth={2.2}
                        aria-hidden="true"
                      />
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      scrollToOrderSection("order-adults-students")
                    }
                    className="flex w-full items-center justify-between gap-4 rounded-[22px] border border-white/50 bg-white/10 px-4 py-3 text-left shadow-[0_16px_36px_rgba(148,163,184,0.14)] backdrop-blur-sm transition hover:bg-white/20"
                  >
                    <span className="min-w-0 text-sm font-medium text-slate-700">
                      Zamów Adults Student&apos;s Book
                    </span>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-transparent bg-[linear-gradient(135deg,#f59e0b,#f97316)] text-white shadow-[0_10px_24px_rgba(249,115,22,0.2)]">
                      <ArrowDown
                        size={18}
                        strokeWidth={2.2}
                        aria-hidden="true"
                      />
                    </span>
                  </button>
                </div>
              </div>

              <aside className="hidden w-full sm:block lg:max-w-[360px] lg:justify-self-end">
                <div className="rounded-[28px] border border-white/50 bg-white/10 p-5 shadow-[0_20px_60px_rgba(148,163,184,0.18)] backdrop-blur-sm sm:p-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[18px] border border-white/30 shadow-[0_14px_30px_rgba(148,163,184,0.36)]">
                      <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-black/5 bg-white/50 text-slate-700">
                        <ShoppingCart
                          size={18}
                          strokeWidth={2.2}
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
                        Zamówienia
                      </p>
                      <p className="mt-1 text-2xl font-semibold tracking-[-0.03em] text-slate-900">
                        Panel zakupów
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 space-y-3">
                    <button
                      type="button"
                      onClick={() =>
                        scrollToOrderSection("order-kids-students")
                      }
                      className="flex w-full items-center justify-between rounded-2xl border border-white/50 bg-white/10 px-4 py-4 text-left transition hover:bg-white/20"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[16px] border border-white/30 shadow-[0_10px_22px_rgba(148,163,184,0.24)]">
                          <div className="flex h-7 w-7 items-center justify-center rounded-xl border border-black/5 bg-white/50 text-slate-700">
                            <ArrowDown
                              size={16}
                              strokeWidth={2.2}
                              aria-hidden="true"
                            />
                          </div>
                        </div>
                        <div className="min-w-0">
                          <span className="block text-sm font-medium text-slate-700">
                            Przejdź do Kids Student&apos;s Book
                          </span>
                          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                            Kids
                          </span>
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        scrollToOrderSection("order-adults-students")
                      }
                      className="flex w-full items-center justify-between rounded-2xl border border-white/50 bg-white/10 px-4 py-4 text-left transition hover:bg-white/20"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[16px] border border-white/30 shadow-[0_10px_22px_rgba(148,163,184,0.24)]">
                          <div className="flex h-7 w-7 items-center justify-center rounded-xl border border-black/5 bg-white/50 text-slate-700">
                            <ArrowDown
                              size={16}
                              strokeWidth={2.2}
                              aria-hidden="true"
                            />
                          </div>
                        </div>
                        <div className="min-w-0">
                          <span className="block text-sm font-medium text-slate-700">
                            Przejdź do Adults Student&apos;s Book
                          </span>
                          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                            Adults
                          </span>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </aside>
            </section>

            <section className="mt-10">
              <div className="hidden rounded-[28px] border border-white/50 bg-white/10 p-2 shadow-[0_20px_60px_rgba(148,163,184,0.28)] backdrop-blur-sm sm:block">
                <div className="grid gap-2 md:grid-cols-2">
                  {tabs.map(
                    ({
                      key,
                      label,
                      description,
                      Icon,
                      accentClassName,
                      iconClassName,
                    }) => {
                      const active = activeTab === key;

                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setActiveTab(key)}
                          className={`rounded-[22px] border p-4 text-left transition ${
                            active
                              ? "border-white/85 bg-sky-50/80 shadow-[0_18px_40px_rgba(148,163,184,0.12)]"
                              : "border-transparent bg-transparent hover:border-white/30 hover:bg-white/10 hover:shadow-[0_12px_28px_rgba(148,163,184,0.16)]"
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div
                              className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-[20px] border shadow-[0_14px_30px_rgba(148,163,184,0.36)] ${
                                active
                                  ? "border-white/55 bg-white/10 shadow-[0_18px_36px_rgba(148,163,184,0.42)]"
                                  : "border-white/30"
                              }`}
                            >
                              <div
                                className={`flex h-10 w-10 items-center justify-center rounded-2xl border border-black/5 shadow-sm ${
                                  active
                                    ? iconClassName
                                    : "!bg-white/45 !text-slate-600"
                                }`}
                              >
                                <Icon
                                  className="h-5 w-5"
                                  strokeWidth={2.2}
                                  aria-hidden="true"
                                />
                              </div>
                            </div>

                            <div className="min-w-0">
                              <p className="bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text text-base font-semibold text-transparent">
                                {label}
                              </p>
                              <p
                                className={`mt-2 flex items-start gap-2 text-sm ${
                                  active ? "text-slate-700" : "text-slate-600"
                                }`}
                              >
                                <span
                                  className={`mt-[0.45rem] h-2 w-2 shrink-0 rounded-full ${
                                    active ? accentClassName : "bg-slate-400"
                                  }`}
                                  aria-hidden="true"
                                />
                                <span>{description}</span>
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    },
                  )}
                </div>
              </div>

              {activeTab === "order" && (
                <div
                  className={`mt-4 space-y-4 ${
                    isCartExpanded ? "pb-[30rem] sm:pb-56" : "pb-36 sm:pb-40"
                  }`}
                >
                  {salesPaused && (
                    <div className="rounded-[28px] border border-amber-200/90 bg-amber-50/85 p-5 text-center text-sm font-medium text-amber-900 shadow-[0_20px_60px_rgba(245,158,11,0.12)] backdrop-blur-md sm:text-base">
                      Z powodu aktualizacji podręczników do drugiego wydania nie
                      wszystkie podręczniki będą dostępne we wrześniu w wersji
                      papierowej. Wersja cyfrowa poniższych pozycji jest już
                      gotowa do użytku.
                    </div>
                  )}

                  {error && (
                    <div className="rounded-[24px] border border-rose-200/90 bg-rose-50/90 px-5 py-4 text-sm font-medium text-rose-700 shadow-[0_20px_60px_rgba(244,63,94,0.12)] backdrop-blur-md">
                      {error}
                    </div>
                  )}

                  <div className="space-y-6">
                    <section
                      id="order-kids-students"
                      className="rounded-[24px] backdrop-blur-sm border border-white/50 p-4 shadow-[0_20px_60px_rgba(148,163,184,0.18)] sm:p-6"
                    >
                      <div className="mb-5">
                        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
                          Kids
                        </p>
                        <h4 className="mt-2 bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text text-xl font-semibold tracking-[-0.03em] text-transparent">
                          Student&apos;s Book
                        </h4>
                      </div>

                      <div className="grid grid-cols-2 gap-4 gap-y-6 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                        {ctx.booksKids?.length > 0 &&
                          ctx.booksKids
                            .sort((a, b) => a.num - b.num)

                            .map((book) => (
                              <BookCardKids
                                key={book.id}
                                book={book}
                                userClient={userClient}
                                selectedBooks={selectedBooks}
                                onQuantityChange={(bookId, quantity) =>
                                  handleQuantityChange(bookId, quantity)
                                }
                              />
                            ))}
                      </div>
                    </section>

                    <section className="rounded-[24px] backdrop-blur-sm border border-white/50 p-4 shadow-[0_20px_60px_rgba(148,163,184,0.18)] sm:p-6">
                      <div className="mb-5">
                        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
                          Kids
                        </p>
                        <h4 className="mt-2 bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text text-xl font-semibold tracking-[-0.03em] text-transparent">
                          Teacher&apos;s Book
                        </h4>
                      </div>

                      <div className="rounded-[24px] border border-white bg-sky-50/40 px-5 py-4 text-sm font-medium leading-6 text-sky-900 shadow-[0_16px_36px_rgba(14,165,233,0.1)]">
                        W tym semestrze Teacher&apos;s Book Kids nie jest
                        jeszcze dostępny. Aby ułatwić pracę nauczycielom, do
                        każdego zamówienia dziecięcego Student&apos;s Book
                        dodajemy <strong>2 dodatkowe kopie gratis</strong>.
                        Dzięki temu lektorzy mają wszystkie materiały potrzebne
                        do prowadzenia zajęć. Przepraszamy za chwilowe
                        utrudnienia, i dziękujemy za wyrozumiałość.
                      </div>
                    </section>

                    <section
                      id="order-adults-students"
                      className="rounded-[24px] backdrop-blur-sm border border-white/50 p-4 shadow-[0_20px_60px_rgba(148,163,184,0.18)] sm:p-6"
                    >
                      <div className="mb-5">
                        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
                          Adults/Teenagers
                        </p>
                        <h4 className="mt-2 bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text text-xl font-semibold tracking-[-0.03em] text-transparent">
                          Student&apos;s Book
                        </h4>
                      </div>

                      <div className="grid grid-cols-2 gap-4 gap-y-6 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                        {ctx.books
                          .filter((b) => b.bookNum.toString().length === 1)
                          .sort((a, b) => a.bookNum - b.bookNum)
                          .map((book) => (
                            <BookCard
                              key={book.id}
                              book={book}
                              selectedBooks={selectedBooks}
                              handleQuantityChange={handleQuantityChange}
                              userClient={userClient}
                            />
                          ))}
                      </div>
                    </section>

                    <section className="rounded-[24px] backdrop-blur-sm border border-white/50 p-4 shadow-[0_20px_60px_rgba(148,163,184,0.18)] sm:p-6">
                      <div className="mb-5 ">
                        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
                          Adults/Teenagers
                        </p>
                        <h4 className="mt-2 bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text text-xl font-semibold tracking-[-0.03em] text-transparent">
                          Teacher&apos;s Book
                        </h4>
                      </div>

                      <div className="grid grid-cols-2 gap-4 gap-y-6 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                        {ctx.books
                          .filter((b) => b.bookNum.toString().length === 2)
                          .sort((a, b) => a.bookNum - b.bookNum)
                          .map((book) => (
                            <BookCard
                              key={book.id}
                              book={book}
                              selectedBooks={selectedBooks}
                              handleQuantityChange={handleQuantityChange}
                              userClient={userClient}
                            />
                          ))}
                      </div>
                    </section>
                  </div>
                </div>
              )}

              {activeTab === "history" && (
                <div className="mt-4 rounded-[28px] border border-white/50  p-4 shadow-[0_20px_60px_rgba(148,163,184,0.18)] backdrop-blur-md sm:p-6">
                  <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
                        Historia zamówień
                      </p>
                      <h3 className="mt-3 bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text text-2xl font-semibold tracking-[-0.03em] text-transparent">
                        Wcześniejsze zakupy i statusy realizacji
                      </h3>
                    </div>
                  </div>

                  {ctx.orders.length === 0 ? (
                    <div className="rounded-[24px] border-dashed border-white/80 bg-white/30 px-5 py-10 text-center text-base font-medium text-slate-600">
                      Brak historii zamówień.
                    </div>
                  ) : (
                    <div className="overflow-x-auto rounded-[24px] border border-orange-100/85 bg-white/40 shadow-[0_12px_30px_rgba(148,163,184,0.12)]">
                      <table className="min-w-full border-separate border-spacing-0">
                        <thead>
                          <tr className="bg-orange-50/70 text-left">
                            <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                              Data
                            </th>
                            <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                              Książki
                            </th>
                            <th className="px-5 py-4 text-center text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                              Ilość
                            </th>
                            <th className="px-5 py-4 text-center text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                              Status
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {ctx.orders
                            .filter((o) => o.clientId === userClient?.id)
                            .sort((a, b) => b.date - a.date)
                            .map((order) => (
                              <tr
                                key={order.id}
                                className="transition-colors hover:bg-white/70"
                              >
                                <td className="border-t border-slate-200/70 px-5 py-4 text-sm font-medium text-slate-700 whitespace-nowrap">
                                  {new Date(order.date).toLocaleDateString()}
                                </td>

                                <td className="border-t border-slate-200/70 px-5 py-4 text-sm text-slate-700">
                                  <div className="flex flex-col items-start gap-2">
                                    {order.values.map((v) => {
                                      const book =
                                        ctx.books.find(
                                          (b) => b.id === v.bookId,
                                        ) ||
                                        ctx.booksKids.find(
                                          (b) => b.id === v.bookId,
                                        );

                                      if (!book) {
                                        return (
                                          <div key={v.bookId}>
                                            Nieznana książka
                                          </div>
                                        );
                                      }

                                      const isAdult = "bookNum" in book;
                                      const ageGroup = isAdult
                                        ? "Adults/Teen"
                                        : "Kids";
                                      const displayNum = isAdult
                                        ? (book as BookNew).bookNum
                                        : (book as KidsBook).num;
                                      const bookType = !isAdult
                                        ? "Student's Book"
                                        : String((book as BookNew).bookNum)
                                              .length === 2
                                          ? "Teacher's Book"
                                          : "Student's Book";

                                      return (
                                        <div key={v.bookId}>
                                          {ageGroup} {bookType} {displayNum}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </td>

                                <td className="border-t border-slate-200/70 px-5 py-4 text-center text-sm font-medium text-slate-700">
                                  <div className="flex flex-col items-center gap-2">
                                    {order.values.map((v) => (
                                      <div key={v.bookId}>{v.quantity}</div>
                                    ))}
                                  </div>
                                </td>

                                <td className="border-t border-slate-200/70 px-5 py-4 text-center text-sm">
                                  <span
                                    className={`inline-flex min-w-[118px] items-center justify-center rounded-full px-3 py-1.5 font-semibold ${
                                      order.status === "completed"
                                        ? "bg-emerald-50 text-emerald-700"
                                        : order.status === "pending"
                                          ? "bg-slate-100 text-slate-600"
                                          : "bg-orange-50 text-orange-600"
                                    }`}
                                  >
                                    {order.status === "completed"
                                      ? "Zrealizowane"
                                      : order.status === "pending"
                                        ? "Oczekuje"
                                        : "W realizacji"}
                                  </span>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </section>

            {activeTab === "order" && (
              <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center px-3 pb-3 sm:px-5 sm:pb-5">
                <div className="pointer-events-auto visible flex w-full max-w-3xl translate-y-0 flex-col rounded-[38px] border border-white/70 bg-white/60 opacity-100 shadow-lg backdrop-blur-lg transition-all duration-500 ease-out">
                  {/* ── Expandable cart items panel ── */}
                  <div
                    className={`overflow-hidden transition-all duration-400 ease-out ${
                      isCartExpanded
                        ? "max-h-[60vh] opacity-100"
                        : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="flex flex-col px-4 pt-4 sm:px-5 sm:pt-5 ">
                      <div className="mb-3 flex items-center justify-between px-3">
                        <p className="text-xs font-medium uppercase tracking-[0.1em] text-slate-700">
                          Pozycje w koszyku
                        </p>
                        <p className="text-xs font-medium text-slate-500">
                          {selectedCopies} szt.
                        </p>
                      </div>

                      {/* Scrollable item list */}
                      <div className="max-h-[calc(60vh-3rem)] space-y-2 overflow-y-auto overscroll-contain pr-1">
                        {cartItems.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center gap-3 rounded-2xl border border-slate-200/60 bg-white/70 px-3 py-2.5 transition-colors hover:bg-white/90"
                          >
                            {/* Thumbnail */}
                            <img
                              src={item.thumbnailSrc}
                              alt={item.label}
                              className="h-14 w-10 shrink-0 rounded-lg border border-slate-200/50 object-cover shadow-sm"
                            />

                            {/* Info */}
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-semibold text-slate-800">
                                {item.label}
                              </p>
                              {item.bookTypeLabel && (
                                <p className="truncate text-xs text-slate-500">
                                  {item.bookTypeLabel}
                                </p>
                              )}
                              {item.subtitle && (
                                <p className="truncate text-xs italic text-slate-400">
                                  {item.subtitle}
                                </p>
                              )}
                            </div>

                            {/* Quantity + price */}
                            <div className="flex shrink-0 items-center gap-2.5">
                              <div className="flex items-center rounded-full border border-slate-200/70 bg-slate-50/80 p-0.5">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleQuantityChange(
                                      item.id,
                                      Math.max(0, item.quantity - 1),
                                    )
                                  }
                                  className="flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition hover:bg-white hover:text-slate-800"
                                  aria-label={`Zmniejsz ilość ${item.label}`}
                                >
                                  <Minus
                                    size={13}
                                    strokeWidth={2.4}
                                    aria-hidden="true"
                                  />
                                </button>
                                <span className="min-w-[1.75rem] text-center text-sm font-bold tabular-nums text-slate-800">
                                  {item.quantity}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleQuantityChange(
                                      item.id,
                                      item.quantity + 1,
                                    )
                                  }
                                  className="flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition hover:bg-white hover:text-slate-800"
                                  aria-label={`Zwiększ ilość ${item.label}`}
                                >
                                  <Plus
                                    size={13}
                                    strokeWidth={2.4}
                                    aria-hidden="true"
                                  />
                                </button>
                              </div>

                              <button
                                type="button"
                                onClick={() => handleQuantityChange(item.id, 0)}
                                className="flex items-center justify-center  text-slate-500 transition  hover:text-rose-500"
                                aria-label={`Usuń ${item.label}`}
                              >
                                <Trash2
                                  size={16}
                                  strokeWidth={2.2}
                                  aria-hidden="true"
                                />
                              </button>

                              <p className="min-w-[4.5rem] text-right text-sm font-semibold tabular-nums text-slate-800">
                                {item.total.toFixed(2)} zł
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Divider between expanded panel and summary */}
                    <div className="mx-4 mt-3 border-t border-slate-200/60 sm:mx-5" />
                  </div>

                  {/* ── Always-visible bottom summary bar ── */}
                  <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-3 p-3 sm:flex sm:flex-row sm:items-center sm:gap-4 sm:p-4">
                    {/* Cart toggle + clear button */}
                    <div className="flex min-w-0 items-center gap-2 self-start sm:shrink-0 sm:gap-3">
                      <button
                        type="button"
                        onClick={handleCartToggle}
                        disabled={!hasSelectedBooks}
                        className="inline-flex min-w-0 items-center justify-between gap-2 rounded-full border border-slate-200/60 bg-white/70 px-3 py-3 text-sm font-medium text-slate-800 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[200px] sm:px-4 sm:text-base"
                      >
                        <div className="flex items-center gap-2">
                          <ShoppingCart
                            size={15}
                            strokeWidth={2.2}
                            aria-hidden="true"
                          />
                          <span>{isCartExpanded ? "Schowaj" : "Pokaż"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="rounded-full bg-sky-600 px-2 py-0.5 text-xs text-white">
                            {selectedCopies}
                          </span>
                          <ChevronUp
                            size={15}
                            strokeWidth={2.2}
                            aria-hidden="true"
                            className={`transition-transform duration-300 ${
                              isCartExpanded ? "" : "rotate-180"
                            }`}
                          />
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={handleResetAll}
                        disabled={!hasSelectedBooks}
                        className="flex items-center justify-center text-slate-700 transition  hover:text-rose-500 disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label="Wyczyść koszyk"
                      >
                        <Trash2
                          size={18}
                          strokeWidth={1.8}
                          aria-hidden="true"
                        />
                      </button>
                    </div>

                    {/* Price summary — discount on top, total below */}
                    <div className="flex min-w-0 flex-col items-end gap-0.5 text-xs sm:flex-1 sm:text-sm">
                      {discountDifference > 0 && (
                        <span className=" font-medium text-green-600">
                          Oszczędzasz: {discountDifference.toFixed(2)} zł
                        </span>
                      )}
                      <span className="font-medium text-slate-900">
                        Do zapłaty: {totalAfterDiscount.toFixed(2)} zł
                      </span>
                    </div>

                    {/* Order CTA */}
                    <button
                      type="button"
                      onClick={handlePlaceOrderClick}
                      disabled={loading || !hasSelectedBooks}
                      className={`col-span-2 flex w-full shrink-0 items-center justify-center gap-2 rounded-full px-6 py-3 sm:w-auto sm:min-w-[200px] sm:text-base ${ACTION_BUTTON_TEXT_CLASS_NAME}`}
                      style={getActionButtonStyle("orange")}
                    >
                      <ShoppingBag
                        size={16}
                        strokeWidth={2.2}
                        aria-hidden="true"
                      />
                      {loading ? "Składam..." : "Złóż zamówienie"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {isModalVisible && (
              <ModalConfirm
                message="Czy na pewno chcesz anulować to zamówienie?"
                onCancel={cancelCancelOrder}
                onConfirm={confirmCancelOrder}
              />
            )}

            {isOrderSuccessModalVisible && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
                <div className="w-full max-w-md rounded-[28px] border border-white/70 bg-white p-6 shadow-[0_24px_80px_rgba(15,23,42,0.18)] sm:p-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                      <CircleCheckBig
                        size={28}
                        strokeWidth={1.8}
                        aria-hidden="true"
                      />
                    </div>
                    <h3 className="mt-4 text-xl font-semibold tracking-[-0.02em] text-slate-900 sm:text-2xl">
                      Dziękujemy!
                    </h3>
                    <h4 className="mt-2 text-sm font-semibold tracking-[-0.02em] text-slate-900 sm:text-lg">
                      Otrzymaliśmy Twoje zamównienie.
                    </h4>
                    <p className="mt-2 text-sm leading-relaxed text-slate-500 sm:text-base">
                      Wkrótce przystąpimy do realizacji i prześlemy fakturę.
                    </p>
                  </div>

                  <div className="mt-6 flex items-start gap-3 rounded-2xl border border-slate-200/60 bg-slate-50/80 px-4 py-3.5">
                    <History
                      size={18}
                      strokeWidth={2.2}
                      aria-hidden="true"
                      className="mt-0.5 shrink-0 text-slate-400"
                    />
                    <p className="text-sm leading-relaxed text-slate-600">
                      Status zamówienia możesz śledzić w zakładce{" "}
                      <span className="font-semibold text-slate-800">
                        Historia Twoich zamówień
                      </span>
                      .
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={handleOrderSuccessAcknowledge}
                    autoFocus
                    className="mt-6 flex w-full items-center justify-center rounded-full bg-slate-900 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 sm:text-base"
                  >
                    Zamknij
                  </button>
                </div>
              </div>
            )}
          </main>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default OrderScreen;
